/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../base.component';
import { Component } from '@angular/core';
import { ModalModel } from '../../../models';
import { ModalService } from '../../../services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-parent-modal',
    templateUrl: './parent-modal.component.html',
    styleUrls: ['./parent-modal.component.scss']
})

export class ParentModalComponent extends BaseComponent {
    /**
     * Z-index of each modal, incremented in the view.
     */
    public index: number = 99;

    /**
     * Array of template objects.
     */
    public templates: ModalModel[] = [];

    /**
     * Modal open state
     */
    public isOpen: boolean = false;

    /**
     * Store if there is a blur in the templates array.
     */
    public templatesContainBlur: boolean;

    constructor(
        private modalService: ModalService,
    ) {
        super();
        this.setupSubscribers();
    }

    /**
     * Tell the modal service that the modal is being dragged.
     * @param state
     */
    public setDragActive(state: boolean): void {
        this.modalService.dragActive = state;
    }

    /**
     * Checks for clicks on the overlay.
     * As the modal body is now embedded in the overlay the modal could be closed unexpectedly.
     * @param $event
     * @param isOverlayClickable
     */
    public overlayClick($event: any, isOverlayClickable: boolean): void {
        const currentTarget = $event && $event.target;
        if (isOverlayClickable && currentTarget.classList?.value?.includes('modal-overlay')) {
            this.removeLatestModal();
        }
    }

    /**
     * Remove latest modal until there is only one and then close the overlay.
     * @private
     */
    private removeLatestModal(): void {
        this.templates.pop();
        if (!this.templates.length) {
            this.isOpen = false;
            this.templates = [];
        }
    }

    /**
     * Subscribe to open and close emitters.
     * @private
     */
    private setupSubscribers(): void {
        this.modalService.openParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: ({ template, id, options }): void => {
                    this.isOpen = true;
                    this.templates.push({ template, id, options });
                    this.templatesContainBlur = this.checkForBlurInTemplates();
                }
            });

        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (id: string): void => {
                    const deleteIndex = this.templates.findIndex(item => item.id === id);
                    if (deleteIndex !== -1) {
                        this.templates.splice(deleteIndex);
                        this.templatesContainBlur = this.checkForBlurInTemplates();
                    }

                    if (!this.templates.length) {
                        this.isOpen = false;
                        this.templates = [];
                    }
                }
            });

        this.modalService.closeAllParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.isOpen = false;
                    this.templates = [];
                }
            });
    }

    /**
     * Check for blur in templates, if one or more overlays are blurred then all should be.
     * @private
     */
    private checkForBlurInTemplates(): boolean {
        return this.templates.some(item => item.options.isOverlayBlurred);
    }
}
